import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Plus, UserCircle, Search, LayoutGrid, List, Mail, Phone, Building2, ChevronRight, Trash2, Pencil } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

const CLIENT_TYPE_ROLES = ['client', 'architect', 'interior_designer', 'consultant', 'external_designer'];
const SALUTATIONS = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];
const GST_TREATMENTS = ['Registered Business', 'Unregistered Business', 'Consumer', 'Overseas', 'SEZ', 'Deemed Export'];
const INDIAN_STATES = ['[AN] - Andaman & Nicobar', '[AP] - Andhra Pradesh', '[AR] - Arunachal Pradesh', '[AS] - Assam', '[BR] - Bihar', '[CG] - Chhattisgarh', '[CH] - Chandigarh', '[DD] - Daman & Diu', '[DL] - Delhi', '[GA] - Goa', '[GJ] - Gujarat', '[HP] - Himachal Pradesh', '[HR] - Haryana', '[JH] - Jharkhand', '[JK] - Jammu & Kashmir', '[KA] - Karnataka', '[KL] - Kerala', '[LA] - Ladakh', '[LD] - Lakshadweep', '[MH] - Maharashtra', '[ML] - Meghalaya', '[MN] - Manipur', '[MP] - Madhya Pradesh', '[MZ] - Mizoram', '[NL] - Nagaland', '[OD] - Odisha', '[PB] - Punjab', '[PY] - Puducherry', '[RJ] - Rajasthan', '[SK] - Sikkim', '[TN] - Tamil Nadu', '[TS] - Telangana', '[TR] - Tripura', '[UK] - Uttarakhand', '[UP] - Uttar Pradesh', '[WB] - West Bengal'];
const PAYMENT_TERMS = ['Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60', '50% Advance', 'Custom'];
const CURRENCIES = ['INR - Indian Rupees', 'USD - US Dollar', 'EUR - Euro', 'GBP - British Pound'];

const formatRoleName = (role) => {
  const roleNames = { client: 'Client', architect: 'Architect', interior_designer: 'Interior Designer', consultant: 'Consultant', external_designer: 'External Designer' };
  return roleNames[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ') : 'Unknown');
};

// Tabbed Edit Form Component
function ClientEditForm({ user, onSave, onCancel, loading }) {
  const [activeTab, setActiveTab] = useState('other_details');
  const [form, setForm] = useState({
    customer_type: user?.customer_type || 'business',
    salutation: user?.salutation || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    company_name: user?.company_name || user?.company || '',
    display_name: user?.display_name || user?.name || '',
    email: user?.email || '',
    work_phone: user?.work_phone || '',
    mobile: user?.mobile || user?.phone || '',
    communication_channels: user?.communication_channels || ['email'],
    role: user?.role || 'client',
    // Other Details
    gst_treatment: user?.gst_treatment || 'Unregistered Business',
    place_of_supply: user?.place_of_supply || '[MH] - Maharashtra',
    pan_number: user?.pan_number || '',
    tax_preference: user?.tax_preference || 'taxable',
    currency: user?.currency || 'INR - Indian Rupees',
    payment_terms: user?.payment_terms || 'Due on Receipt',
    // Address
    billing_address: user?.billing_address || '',
    billing_city: user?.billing_city || '',
    billing_state: user?.billing_state || '',
    billing_pincode: user?.billing_pincode || '',
    billing_country: user?.billing_country || 'India',
    // Remarks
    remarks: user?.remarks || '',
    // iter-308: Reset password (write-only)
    password: '',
  });

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const toggleChannel = (ch) => {
    const chs = form.communication_channels.includes(ch)
      ? form.communication_channels.filter(c => c !== ch)
      : [...form.communication_channels, ch];
    set('communication_channels', chs);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...form, name: form.display_name || `${form.first_name} ${form.last_name}`.trim() };
    // iter-308: only send password if user actually typed one (blank = keep current)
    if (!payload.password) delete payload.password;
    onSave(payload);
  };

  const tabs = [
    { id: 'other_details', label: 'Other Details' },
    { id: 'address', label: 'Address' },
    { id: 'remarks', label: 'Remarks' },
    { id: 'password', label: 'Reset Password' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Customer Type */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
        <Label className="text-sm text-stone-600 text-right">Customer Type</Label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="customer_type" checked={form.customer_type === 'business'} onChange={() => set('customer_type', 'business')} className="accent-stone-800" />
            <span className="text-sm">Business</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="customer_type" checked={form.customer_type === 'individual'} onChange={() => set('customer_type', 'individual')} className="accent-stone-800" />
            <span className="text-sm">Individual</span>
          </label>
        </div>
      </div>
      {/* Primary Contact */}
      <div className="grid grid-cols-[140px_1fr] items-start gap-x-4">
        <Label className="text-sm text-stone-600 text-right pt-2">Primary Contact</Label>
        <div className="flex gap-2">
          <Select value={form.salutation} onValueChange={(v) => set('salutation', v)}>
            <SelectTrigger className="w-28" data-testid="edit-salutation"><SelectValue placeholder="Salutation" /></SelectTrigger>
            <SelectContent>{SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="First Name" className="flex-1" data-testid="edit-first-name" />
          <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} placeholder="Last Name" className="flex-1" data-testid="edit-last-name" />
        </div>
      </div>
      {/* Company Name */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
        <Label className="text-sm text-stone-600 text-right">Company Name</Label>
        <Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Company Name" className="max-w-sm" data-testid="edit-company" />
      </div>
      {/* Display Name */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
        <Label className="text-sm text-red-600 text-right font-semibold">Display Name *</Label>
        <Input required value={form.display_name} onChange={(e) => set('display_name', e.target.value)} className="max-w-sm" data-testid="edit-display-name" />
      </div>
      {/* Email */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
        <Label className="text-sm text-stone-600 text-right">Email Address</Label>
        <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="max-w-sm" data-testid="edit-email" />
      </div>
      {/* Phone */}
      <div className="grid grid-cols-[140px_1fr] items-start gap-x-4">
        <Label className="text-sm text-stone-600 text-right pt-2">Phone</Label>
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-1 items-center">
            <span className="text-xs text-stone-400 bg-stone-100 px-2 py-1.5 rounded border">+91</span>
            <Input value={form.work_phone} onChange={(e) => set('work_phone', e.target.value)} placeholder="Work Phone" className="w-36" data-testid="edit-work-phone" />
          </div>
          <div className="flex gap-1 items-center">
            <span className="text-xs text-stone-400 bg-stone-100 px-2 py-1.5 rounded border">+91</span>
            <Input value={form.mobile} onChange={(e) => set('mobile', e.target.value)} placeholder="Mobile" className="w-36" data-testid="edit-mobile" />
          </div>
        </div>
      </div>
      {/* Role */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
        <Label className="text-sm text-stone-600 text-right">Role</Label>
        <Select value={form.role} onValueChange={(v) => set('role', v)}>
          <SelectTrigger className="max-w-sm" data-testid="edit-role"><SelectValue /></SelectTrigger>
          <SelectContent>{CLIENT_TYPE_ROLES.map(r => <SelectItem key={r} value={r}>{formatRoleName(r)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {/* Communication Channels */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
        <Label className="text-sm text-stone-600 text-right">Communication Channels</Label>
        <div className="flex gap-4">
          {['email', 'sms', 'whatsapp'].map(ch => (
            <label key={ch} className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.communication_channels.includes(ch)} onCheckedChange={() => toggleChannel(ch)} />
              <span className="text-sm capitalize">{ch === 'sms' ? 'SMS' : ch === 'whatsapp' ? 'WhatsApp' : 'Email'}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-stone-200 mt-6">
        <div className="flex gap-0">
          {tabs.map(tab => (
            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
            >{tab.label}</button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'other_details' && (
        <div className="space-y-5 pt-2">
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-red-600 text-right font-semibold">GST Treatment *</Label>
            <Select value={form.gst_treatment} onValueChange={(v) => set('gst_treatment', v)}>
              <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{GST_TREATMENTS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-red-600 text-right font-semibold">Place of Supply *</Label>
            <Select value={form.place_of_supply} onValueChange={(v) => set('place_of_supply', v)}>
              <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-stone-600 text-right">PAN</Label>
            <Input value={form.pan_number} onChange={(e) => set('pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" className="max-w-sm" maxLength={10} data-testid="edit-pan" />
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-red-600 text-right font-semibold">Tax Preference *</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="tax_preference" checked={form.tax_preference === 'taxable'} onChange={() => set('tax_preference', 'taxable')} className="accent-blue-600" />
                <span className="text-sm">Taxable</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="tax_preference" checked={form.tax_preference === 'tax_exempt'} onChange={() => set('tax_preference', 'tax_exempt')} className="accent-blue-600" />
                <span className="text-sm">Tax Exempt</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-stone-600 text-right">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
              <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-stone-600 text-right">Payment Terms</Label>
            <Select value={form.payment_terms} onValueChange={(v) => set('payment_terms', v)}>
              <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_TERMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}

      {activeTab === 'address' && (
        <div className="space-y-5 pt-2">
          <div className="grid grid-cols-[140px_1fr] items-start gap-x-4">
            <Label className="text-sm text-stone-600 text-right pt-2">Billing Address</Label>
            <Textarea value={form.billing_address} onChange={(e) => set('billing_address', e.target.value)} placeholder="Street address, apartment, suite, etc." rows={2} className="max-w-lg" />
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-stone-600 text-right">City</Label>
            <Input value={form.billing_city} onChange={(e) => set('billing_city', e.target.value)} placeholder="City" className="max-w-sm" />
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-stone-600 text-right">State</Label>
            <Input value={form.billing_state} onChange={(e) => set('billing_state', e.target.value)} placeholder="State" className="max-w-sm" />
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-stone-600 text-right">PIN Code</Label>
            <Input value={form.billing_pincode} onChange={(e) => set('billing_pincode', e.target.value)} placeholder="PIN Code" className="max-w-xs" maxLength={6} />
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
            <Label className="text-sm text-stone-600 text-right">Country</Label>
            <Input value={form.billing_country} onChange={(e) => set('billing_country', e.target.value)} className="max-w-sm" />
          </div>
        </div>
      )}

      {activeTab === 'remarks' && (
        <div className="space-y-5 pt-2">
          <div className="grid grid-cols-[140px_1fr] items-start gap-x-4">
            <Label className="text-sm text-stone-600 text-right pt-2">Remarks</Label>
            <Textarea value={form.remarks} onChange={(e) => set('remarks', e.target.value)} placeholder="Internal notes about this client..." rows={4} className="max-w-lg" />
          </div>
        </div>
      )}

      {/* iter-308: Reset Password tab */}
      {activeTab === 'password' && (
        <div className="space-y-4 pt-2">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 max-w-lg">
            <p className="text-sm text-amber-900 mb-3">
              Set a new password for this client. Leave blank to keep the current one.
              Saving with a new password will <strong>force-sign-out</strong> this client from every device.
            </p>
            <div className="grid grid-cols-[140px_1fr] items-center gap-x-4">
              <Label className="text-sm text-stone-700 text-right">New Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
                data-testid="client-edit-password"
              />
            </div>
          </div>
        </div>
      )}

      {/* Save / Cancel */}
      <div className="flex gap-3 pt-4 border-t border-stone-200">
        <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-client-btn">
          {loading ? 'Saving...' : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

export default function ClientsList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'client' });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter(u => CLIENT_TYPE_ROLES.includes(u.role) && u.status !== 'deleted'));
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (e, user) => {
    e.stopPropagation();
    if (!window.confirm(`Delete client "${user.display_name || user.name}"?\n\nThe client will be hidden from all lists. Their past projects, invoices and transactions remain intact for audit.`)) return;
    try {
      await axios.delete(`${API}/clients/${user.id}`);
      toast.success('Client deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success('Client created successfully');
      setDialogOpen(false);
      setFormData({ email: '', password: '', name: '', role: 'client' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (payload) => {
    if (!editingUser) return;
    setEditLoading(true);
    try {
      const passwordChanged = !!payload.password;
      await axios.put(`${API}/users/${editingUser.id}`, payload);
      // iter-308: if password was reset, force-logout all the client's sessions.
      if (passwordChanged) {
        try { await axios.post(`${API}/users/${editingUser.id}/logout-everywhere`); } catch {}
        toast.success(`Password reset for ${editingUser.name}. All their sessions have been revoked.`);
      } else {
        toast.success('Client updated successfully');
      }
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally {
      setEditLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(u =>
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="clients-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-stone-900">Clients</h1>
            <p className="text-stone-500 mt-1">Manage all client accounts ({filteredUsers.length})</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
              <Input placeholder="Search clients..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-64" data-testid="clients-search" />
            </div>
            <div className="flex bg-stone-100 rounded-lg p-0.5">
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}><LayoutGrid size={16} /></button>
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}><List size={16} /></button>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2" data-testid="add-client-btn"><Plus size={16} /> Add Client</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add New Client</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div><Label>Name</Label><Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
                  <div><Label>Email</Label><Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
                  <div><Label>Password</Label><Input type="password" required value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} /></div>
                  <div>
                    <Label>Role</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CLIENT_TYPE_ROLES.map(r => <SelectItem key={r} value={r}>{formatRoleName(r)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="btn-primary">Create Client</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900" /></div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-16 text-center">
            <UserCircle size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">No clients found</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="clients-list">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Company</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase">GST Treatment</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-600 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-stone-50 cursor-pointer transition-colors" onClick={() => openEditDialog(user)} data-testid={`client-row-${user.id}`}>
                    <td className="px-4 py-3"><span className="font-medium text-stone-900">{user.display_name || user.name}</span></td>
                    <td className="px-4 py-3 text-sm text-stone-600">{user.company_name || user.company || '-'}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{formatRoleName(user.role)}</Badge></td>
                    <td className="px-4 py-3 text-sm text-stone-600">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{user.mobile || user.phone || user.work_phone || '-'}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{user.gst_treatment || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditDialog(user); }}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-800 transition-colors mr-1"
                        title="Edit / Reset password"
                        data-testid={`edit-client-${user.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDeleteClient(e, user)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors mr-1"
                        title="Delete client"
                        data-testid={`delete-client-${user.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                      <ChevronRight size={16} className="text-stone-400 inline-block" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
            {filteredUsers.map(user => (
              <div key={user.id} className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow cursor-pointer relative group" onClick={() => openEditDialog(user)} data-testid={`client-card-${user.id}`}>
                <div className="absolute top-3 right-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditDialog(user); }}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                    title="Edit / Reset password"
                    data-testid={`edit-client-card-${user.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={(e) => handleDeleteClient(e, user)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-500 hover:bg-red-50 hover:text-red-700"
                    title="Delete client"
                    data-testid={`delete-client-card-${user.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold text-lg">{(user.display_name || user.name)?.[0]?.toUpperCase() || '?'}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-stone-900 truncate">{user.display_name || user.name}</h3>
                    <Badge variant="outline" className="text-xs mt-1">{formatRoleName(user.role)}</Badge>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {user.company_name && <div className="flex items-center gap-2 text-sm text-stone-500"><Building2 size={13} />{user.company_name}</div>}
                  {user.email && <div className="flex items-center gap-2 text-sm text-stone-500"><Mail size={13} /><span className="truncate">{user.email}</span></div>}
                  {(user.mobile || user.phone) && <div className="flex items-center gap-2 text-sm text-stone-500"><Phone size={13} />{user.mobile || user.phone}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Client Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-3xl w-[90vw] max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
            <DialogHeader><DialogTitle className="text-lg">Edit Customer</DialogTitle></DialogHeader>
            {editingUser && (
              <ClientEditForm user={editingUser} onSave={handleUpdateUser} onCancel={() => setEditDialogOpen(false)} loading={editLoading} />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
